import { queryKeys } from '@query/keys'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type {
  AvailableDashboardWidgetDto,
  ClientDashboardLayoutDto,
  ClientDashboardWidgetDto,
  CreateDashboardLayoutPayload,
  UpdateDashboardLayoutPayload,
  WidgetOptionDto,
  WidgetSizeDto,
} from './dashboardApi.types'
import { WidgetType, isWidgetType } from './types'
import type { DashboardLayout, Widget } from './types'
import { getAvailableWidgets } from './utils/dashboardWidget.utils'
import { arrangeMobileWidgets } from './widgetLayouts'

const IB_DASHBOARD_TYPE_ID = 1

function mapWidgetDtoToWidget(dto: ClientDashboardWidgetDto, size: WidgetSizeDto): Widget | null {
  if (!isWidgetType(dto.type)) {
    return null
  }

  return {
    id: dto.id,
    type: dto.type,
    size,
    title: dto.name,
    layout: dto.layout
      ? {
          i: dto.id,
          x: dto.layout.x,
          y: dto.layout.y,
          w: dto.layout.w,
          h: dto.layout.h,
        }
      : null,
    mobileLayout: dto.mobileLayout
      ? {
          i: dto.id,
          x: dto.mobileLayout.x,
          y: dto.mobileLayout.y,
          w: dto.mobileLayout.w,
          h: dto.mobileLayout.h,
        }
      : null,
    inUse: dto.inUse,
  }
}

function mapLayoutDtoToLayout(
  dto: ClientDashboardLayoutDto,
  availableSizes: ReadonlyMap<string, WidgetSizeDto>
): DashboardLayout {
  const widgets = dto.widgets.flatMap((widgetDto) => {
    const size = availableSizes.get(widgetDto.id)
    const widget = size ? mapWidgetDtoToWidget(widgetDto, size) : null

    return widget ? [widget] : []
  })

  return {
    id: dto.id,
    name: dto.name,
    description: dto.description,
    isPreset: dto.isPreset,
    widgets: arrangeMobileWidgets(widgets),
    isDefault: dto.isDefault,
  }
}

function mapAvailableWidgetToWidget(dto: AvailableDashboardWidgetDto): Widget | null {
  if (!isWidgetType(dto.type)) {
    return null
  }

  return {
    id: dto.id,
    type: dto.type,
    size: dto.size,
    title: dto.name,
    layout: null,
    mobileLayout: null,
    inUse: dto.inUse,
  }
}

function buildWidgetOptions(widgets: Widget[], mode: 'desktop' | 'mobile'): WidgetOptionDto[] {
  const arrangedWidgets = mode === 'mobile' ? arrangeMobileWidgets(widgets) : widgets

  return arrangedWidgets.flatMap((widget) => {
    const layout = mode === 'mobile' ? widget.mobileLayout : widget.layout

    if (!layout) {
      return []
    }

    return [
      {
        id: widget.id,
        layout: {
          x: layout.x,
          y: layout.y,
          w: layout.w,
          h: layout.h,
        },
      },
    ]
  })
}

interface UseDashboardResult {
  layouts: DashboardLayout[]
  availableWidgets: Widget[]
  isLoading: boolean
  error: Error | null
  createLayout: (
    name: string,
    description: string,
    widgets: Widget[],
    isDefault?: boolean
  ) => Promise<string>
  updateLayout: (
    layoutId: string,
    name: string,
    description: string | undefined,
    widgets: Widget[],
    isDefault?: boolean
  ) => Promise<void>
  deleteLayout: (layoutId: string) => Promise<void>
}

export function useDashboard(): UseDashboardResult {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const locale = useSessionLanguage()
  const layoutsQueryKey = queryKeys.ib.dashboardLayouts(locale)

  const {
    data: rawLayouts,
    isLoading: layoutsLoading,
    error: layoutsError,
  } = useQuery({
    queryKey: layoutsQueryKey,
    queryFn: () => apiClient.getClientDashboardLayouts([IB_DASHBOARD_TYPE_ID], locale),
  })

  const {
    data: rawAvailable,
    isLoading: availableLoading,
    error: availableError,
  } = useQuery({
    queryKey: queryKeys.ib.availableDashboardLayouts(locale),
    queryFn: () => apiClient.getAvailableDashboardLayouts([IB_DASHBOARD_TYPE_ID], locale),
  })

  const availableDashboardWidgets = useMemo(
    () => getAvailableWidgets(rawAvailable ?? []),
    [rawAvailable]
  )

  const availableSizes = useMemo(
    () => new Map(availableDashboardWidgets.map(({ id, size }) => [id, size])),
    [availableDashboardWidgets]
  )

  const layouts = useMemo(
    () => rawLayouts?.map((dto) => mapLayoutDtoToLayout(dto, availableSizes)) ?? [],
    [rawLayouts, availableSizes]
  )

  const availableWidgets = useMemo(() => {
    const widgetTypes = new Set<WidgetType>()

    return availableDashboardWidgets.flatMap((dto) => {
      const widget = mapAvailableWidgetToWidget(dto)

      if (!widget || widgetTypes.has(widget.type)) {
        return []
      }

      widgetTypes.add(widget.type)
      return [widget]
    })
  }, [availableDashboardWidgets])

  const createLayoutMutation = useMutation({
    mutationFn: (payload: CreateDashboardLayoutPayload) => apiClient.createDashboardLayout(payload),
  })

  const updateLayoutMutation = useMutation({
    mutationFn: ({
      layoutId,
      payload,
    }: {
      layoutId: string
      payload: UpdateDashboardLayoutPayload
    }) => apiClient.updateDashboardLayout(layoutId, payload),
    onMutate: async ({ layoutId, payload }) => {
      if (payload.isDefault !== true) {
        return
      }

      await queryClient.cancelQueries({ queryKey: layoutsQueryKey })

      const previousLayouts = queryClient.getQueryData<ClientDashboardLayoutDto[]>(layoutsQueryKey)

      queryClient.setQueryData<ClientDashboardLayoutDto[]>(layoutsQueryKey, (currentLayouts) =>
        (currentLayouts ?? []).map((layout) => ({
          ...layout,
          isDefault: layout.id === layoutId,
        }))
      )

      return { previousLayouts }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousLayouts) {
        queryClient.setQueryData(layoutsQueryKey, context.previousLayouts)
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: layoutsQueryKey }),
  })

  const deleteLayoutMutation = useMutation({
    mutationFn: (layoutId: string) => apiClient.deleteDashboardLayout(layoutId),
    onMutate: async (layoutId) => {
      await queryClient.cancelQueries({ queryKey: layoutsQueryKey })

      const previousLayouts = queryClient.getQueryData<ClientDashboardLayoutDto[]>(layoutsQueryKey)

      queryClient.setQueryData<ClientDashboardLayoutDto[]>(layoutsQueryKey, (currentLayouts) =>
        (currentLayouts ?? []).filter((layout) => layout.id !== layoutId)
      )

      return { previousLayouts }
    },
    onError: (_error, _layoutId, context) => {
      if (context?.previousLayouts) {
        queryClient.setQueryData(layoutsQueryKey, context.previousLayouts)
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: layoutsQueryKey }),
  })

  const createLayout = async (
    name: string,
    description: string,
    widgets: Widget[],
    isDefault?: boolean
  ) => {
    const existingLayoutIds = new Set((rawLayouts ?? []).map(({ id }) => id))
    const payload: CreateDashboardLayoutPayload = {
      name,
      description,
      dashboardTypeId: IB_DASHBOARD_TYPE_ID,
      widgetOptions: buildWidgetOptions(widgets, 'desktop'),
      // Commented out until we have proper mobile widgets logic
      // mobileWidgetOptions: buildWidgetOptions(widgets, 'mobile'),
      isDefault,
    }

    await createLayoutMutation.mutateAsync(payload)

    const updatedLayouts = await apiClient.getClientDashboardLayouts([IB_DASHBOARD_TYPE_ID], locale)
    queryClient.setQueryData(layoutsQueryKey, updatedLayouts)

    const createdLayout = updatedLayouts.find(({ id }) => !existingLayoutIds.has(id))

    if (!createdLayout) {
      throw new Error('Created layout not found after refresh')
    }

    return createdLayout.id
  }

  const updateLayout = async (
    layoutId: string,
    name: string,
    description: string | undefined,
    widgets: Widget[],
    isDefault?: boolean
  ) => {
    if (!layoutId) {
      throw new Error('Layout ID is required for update')
    }

    const payload: UpdateDashboardLayoutPayload = {
      name,
      description,
      dashboardTypeId: IB_DASHBOARD_TYPE_ID,
      widgetOptions: buildWidgetOptions(widgets, 'desktop'),
      // Commented out until we have proper mobile widgets logic
      // mobileWidgetOptions: buildWidgetOptions(widgets, 'mobile'),
      isDefault,
    }

    await updateLayoutMutation.mutateAsync({ layoutId, payload })
  }

  const deleteLayout = async (layoutId: string) => {
    if (!layoutId) {
      throw new Error('Layout ID is required for deletion')
    }

    await deleteLayoutMutation.mutateAsync(layoutId)
  }

  return {
    layouts,
    availableWidgets,
    isLoading: layoutsLoading || availableLoading,
    error: layoutsError ?? availableError,
    createLayout,
    updateLayout,
    deleteLayout,
  }
}
